// One eLab test still waiting for its result (KIN-2087), as the worklist reads it.
//
// The backend (KIN-2085) already decides what is waiting and what is overdue — it
// holds the turnaround figures, we do not — so nothing here recomputes either. What
// this file does is turn the DTO into the words and the ordering the worklist shows,
// and that is the part worth testing on its own.

export const LAB_TEST_STATUS = {
  outstanding: "OUTSTANDING",
  partial: "PARTIAL",
  resulted: "RESULTED",
  closed: "CLOSED",
};

// Only the two waiting statuses are ever shown. The labels are the GP's words, not the
// enum's: "OUTSTANDING" means nothing back yet, and "PARTIAL" means a preliminary
// result arrived and the final one is still due — which is not obvious from "Partial".
export const LAB_TEST_STATUS_CONFIG = {
  [LAB_TEST_STATUS.outstanding]: { label: "Waiting" },
  [LAB_TEST_STATUS.partial]: { label: "Preliminary result" },
  [LAB_TEST_STATUS.resulted]: { label: "Resulted" },
  [LAB_TEST_STATUS.closed]: { label: "Closed" },
};

export const getLabTestStatusLabel = (status) =>
  LAB_TEST_STATUS_CONFIG[status]?.label ?? "-";

// RESULTED and CLOSED never appear on a worklist. The backend already filters them
// out; this is the guard that keeps a stale cache or a changed backend from putting a
// finished test in front of a GP as if it were still due.
export const isWaitingLabTest = (test) =>
  test?.status === LAB_TEST_STATUS.outstanding ||
  test?.status === LAB_TEST_STATUS.partial;

// The lab's own name for the test when it gave one, its code when it did not. A GP can
// act on "Thyroid stimulating hormone" and sometimes on "TSH"; they can act on neither
// when both are missing, so the dash says so rather than leaving the cell blank.
export const getLabTestLabel = (test) => test?.testName || test?.testCode || "-";

const toTime = (value) => {
  const time = value ? new Date(value).getTime() : NaN;

  return Number.isNaN(time) ? null : time;
};

// Ascending by a time, with a missing time last — a missing date is not evidence of age
// or of urgency, so it never takes a place near the top of the list.
const compareTimesMissingLast = (firstValue, secondValue) => {
  const firstTime = toTime(firstValue);
  const secondTime = toTime(secondValue);

  if (firstTime === null && secondTime === null) return 0;
  if (firstTime === null) return 1;
  if (secondTime === null) return -1;

  return firstTime - secondTime;
};

const compareOldestFirst = (first, second) =>
  compareTimesMissingLast(first?.requestedAt, second?.requestedAt);

/**
 * Oldest first — the whole point of the list is the test nobody has noticed, and that
 * is the one at the top. A test with no requested time sorts last rather than first:
 * a missing date is not evidence of age, and letting it lead the list would push the
 * genuinely old tests down.
 */
export const sortByOldestFirst = (tests = []) =>
  [...tests].sort(compareOldestFirst);

/*
 * Overdue first (KIN-2179). An overdue test is one the lab should already have answered,
 * so it is the one a GP most needs to chase — and oldest first alone can bury it under
 * older tests that are not due yet. Among the overdue, the one that went overdue
 * earliest leads, even when it was requested later: a slow test can go overdue before a
 * fast one. Everything else stays oldest first.
 *
 * The backend returns this order already, but the list is re-filtered here, so the
 * order is kept here too rather than trusted to survive.
 */
const compareOverdueFirst = (first, second) => {
  const isFirstOverdue = !!first?.overdue;
  const isSecondOverdue = !!second?.overdue;

  if (isFirstOverdue !== isSecondOverdue) return isFirstOverdue ? -1 : 1;

  if (isFirstOverdue) {
    const byOverdueAt = compareTimesMissingLast(
      first?.overdueAt,
      second?.overdueAt
    );

    if (byOverdueAt !== 0) return byOverdueAt;
  }

  return compareOldestFirst(first, second);
};

export const sortByOverdueFirst = (tests = []) =>
  [...tests].sort(compareOverdueFirst);

/**
 * The rows the practice worklist shows: waiting tests only, overdue first, then oldest
 * first.
 */
export const toWorklistRows = (tests = []) =>
  sortByOverdueFirst(tests.filter(isWaitingLabTest));

const compareNewestFirst = (first, second) => {
  const firstTime = toTime(first?.requestedAt);
  const secondTime = toTime(second?.requestedAt);

  if (firstTime === null && secondTime === null) return 0;
  if (firstTime === null) return 1;
  if (secondTime === null) return -1;

  return secondTime - firstTime;
};

// A–Z ignoring case, with a missing name last: a blank cell at the top would read as
// the first patient, GP or section rather than as one the message never named.
const compareTextMissingLast = (firstValue, secondValue) => {
  const firstText = String(firstValue ?? "").trim();
  const secondText = String(secondValue ?? "").trim();

  if (!firstText && !secondText) return 0;
  if (!firstText) return 1;
  if (!secondText) return -1;

  return firstText.localeCompare(secondText, undefined, { sensitivity: "base" });
};

// Grouping by a name only says who or where; within the group the oldest test is still
// the one to chase, so every A–Z option falls back to oldest first.
const byTextThenOldestFirst = (field) => (first, second) =>
  compareTextMissingLast(first?.[field], second?.[field]) ||
  compareOldestFirst(first, second);

export const OUTSTANDING_SORT = {
  overdueFirst: "overdue-first",
  oldestFirst: "oldest-first",
  newestFirst: "newest-first",
  patient: "patient",
  orderingGp: "ordering-gp",
  section: "section",
};

// The sort menu (KIN-2178). Overdue first is the default because it is the order the
// list exists for; the others let a GP work through it by patient, GP or lab section.
export const OUTSTANDING_SORT_OPTIONS = [
  { field: OUTSTANDING_SORT.overdueFirst, direction: "", label: "Overdue first" },
  { field: OUTSTANDING_SORT.oldestFirst, direction: "", label: "Oldest first" },
  { field: OUTSTANDING_SORT.newestFirst, direction: "", label: "Newest first" },
  { field: OUTSTANDING_SORT.patient, direction: "", label: "Patient" },
  { field: OUTSTANDING_SORT.orderingGp, direction: "", label: "Ordering GP" },
  { field: OUTSTANDING_SORT.section, direction: "", label: "Lab section" },
];

export const DEFAULT_OUTSTANDING_SORT = {
  field: OUTSTANDING_SORT.overdueFirst,
  direction: "",
};

const OUTSTANDING_SORT_COMPARATORS = {
  [OUTSTANDING_SORT.overdueFirst]: compareOverdueFirst,
  [OUTSTANDING_SORT.oldestFirst]: compareOldestFirst,
  [OUTSTANDING_SORT.newestFirst]: compareNewestFirst,
  [OUTSTANDING_SORT.patient]: byTextThenOldestFirst("patientName"),
  [OUTSTANDING_SORT.orderingGp]: byTextThenOldestFirst("orderingStaffName"),
  [OUTSTANDING_SORT.section]: byTextThenOldestFirst("section"),
};

/**
 * The worklist in the order the GP picked. An unknown option falls back to the default
 * rather than to whatever order the rows happen to be in.
 */
export const sortWorklistRows = (rows = [], sortField) => {
  const compare =
    OUTSTANDING_SORT_COMPARATORS[sortField] ?? compareOverdueFirst;

  return [...rows].sort(compare);
};

/*
 * The filter options are derived from the list itself rather than from a fixed table of
 * lab sections, because the section is a local code (OBR.24) that each hospital spells
 * its own way — a hard-coded list would offer sections nobody orders and hide the ones
 * they do. The cost is that an option exists only while something is waiting under it,
 * which is exactly when the filter is of any use.
 */
const toOptions = (values) =>
  [...new Set(values.filter(Boolean))].sort((first, second) =>
    String(first.label ?? first).localeCompare(String(second.label ?? second))
  );

export const getSectionOptions = (tests = []) =>
  toOptions(tests.map((test) => test?.section)).map((section) => ({
    value: section,
    label: section,
  }));

export const getOrderingGpOptions = (tests = []) => {
  const byId = new Map();

  tests.forEach((test) => {
    if (test?.orderingStaffId === null || test?.orderingStaffId === undefined) {
      return;
    }

    const id = String(test.orderingStaffId);

    if (!byId.has(id)) {
      byId.set(id, { value: id, label: test.orderingStaffName || `Staff ${id}` });
    }
  });

  return [...byId.values()].sort((first, second) =>
    first.label.localeCompare(second.label)
  );
};

/**
 * The three filters, applied together. Each is ignored when it is not set, so an
 * unfiltered list is the full worklist rather than an empty one.
 *
 * `orderingStaffId` is compared as a string: it arrives from the filter menu as the
 * option's value and from the API as a number, and `===` between the two is false.
 */
export const filterWorklistRows = (
  rows = [],
  { overdueOnly = false, orderingStaffId = null, section = null } = {}
) =>
  rows.filter((row) => {
    if (overdueOnly && !row?.overdue) return false;

    if (
      orderingStaffId !== null &&
      orderingStaffId !== undefined &&
      String(row?.orderingStaffId) !== String(orderingStaffId)
    ) {
      return false;
    }

    if (
      section &&
      String(row?.section ?? "").toUpperCase() !== String(section).toUpperCase()
    ) {
      return false;
    }

    return true;
  });

export const countOverdue = (tests = []) =>
  tests.filter((test) => test?.overdue).length;

const isTrackedProgress = (testProgress) =>
  Number.isFinite(testProgress?.total) && testProgress.total > 0;

/*
 * The count half of the sentence, from the order's own testProgress (KIN-2176/2177).
 *
 * It used to be testNames minus the outstanding list, which read "All 6 back" for an
 * order with nothing back whenever the list had nothing for it: an order downloaded
 * before tracking existed, the list still loading or failed, or a test closed as "not
 * coming back". A GP was told results were in when they were not.
 *
 * Now only resulted tests count as back. A preliminary result is not back, a closed test
 * is said separately and never counted as back, and "All N back" is said only when the
 * backend says allResulted. No testProgress means the tests are not tracked — never that
 * they are all back — so there is no count at all.
 */
const getCountSentence = (testProgress) => {
  if (!isTrackedProgress(testProgress)) return null;

  const { total, resulted = 0, closed = 0, allResulted } = testProgress;

  if (allResulted === true) return `All ${total} back`;

  const backSentence = `${resulted} of ${total} back`;

  return closed > 0 ? `${backSentence}, ${closed} closed` : backSentence;
};

/**
 * What one order on the patient's screen still owes: "2 of 4 back, 1 closed. Vitamin D
 * outstanding, overdue".
 *
 * The count comes from `testProgress` alone. The outstanding list only names the waiting
 * tests and raises the overdue flag, and only once it has loaded successfully
 * (`isOutstandingLoaded`) — a list still loading, or one that failed, says nothing about
 * what is waiting.
 */
export const summariseOrderOutstanding = ({
  testProgress,
  outstandingTests = [],
  isOutstandingLoaded = false,
}) => {
  const countSentence = getCountSentence(testProgress);

  // Untracked: there is nothing honest to say, so the cell stays empty.
  if (countSentence === null) {
    return { outstandingCount: 0, overdueCount: 0, names: [], summary: null };
  }

  const waiting = isOutstandingLoaded
    ? outstandingTests.filter(isWaitingLabTest)
    : [];
  const names = sortByOldestFirst(waiting).map(getLabTestLabel);
  const overdueCount = countOverdue(waiting);

  if (waiting.length === 0) {
    return { outstandingCount: 0, overdueCount: 0, names: [], summary: countSentence };
  }

  const namesSentence = `${names.join(", ")} outstanding${
    overdueCount > 0 ? ", overdue" : ""
  }`;

  return {
    outstandingCount: waiting.length,
    overdueCount,
    names,
    summary: `${countSentence}. ${namesSentence}`,
  };
};

/**
 * A close that the backend refused because the test had been resulted or closed in the
 * meantime (409). It is not an error the GP caused and not one they can fix — the list
 * they were looking at was simply out of date — so the caller refreshes rather than
 * reporting a failure.
 */
export const isAlreadySettledError = (error) =>
  error?.response?.status === 409 || error?.response?.data?.code === "CONFLICT";
